import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * «Escoge cómo verlo» (tanda 6): control segmentado de vistas (lista, tarjetas, tablero…).
 * Sin estado: quien lo usa guarda la elección (ver `usePreferencia`). En pantallas angostas
 * (menos de `sm`) solo se ve el icono; el nombre queda en `title` y `aria-label`.
 */
export interface OpcionDeVista<T extends string> {
  id: T;
  etiqueta: string;
  icono: LucideIcon;
  /** Frase corta que sale al pasar el mouse; sin ella, sale el nombre. */
  ayuda?: string;
}

export function SelectorDeVista<T extends string>({
  opciones,
  valor,
  onCambiar,
  etiqueta,
  className,
}: {
  opciones: OpcionDeVista<T>[];
  valor: T;
  onCambiar: (id: T) => void;
  /** Nombre del grupo para lectores de pantalla, p. ej. «Cómo ver la cartera». */
  etiqueta: string;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={etiqueta}
      className={cn('inline-flex overflow-hidden rounded-lg border border-surface-border dark:border-slate-700', className)}
    >
      {opciones.map(({ id, etiqueta: nombre, icono: Icono, ayuda }) => {
        const activa = id === valor;
        return (
          <button
            key={id}
            type="button"
            aria-pressed={activa}
            aria-label={nombre}
            title={ayuda ?? nombre}
            onClick={() => onCambiar(id)}
            className={cn(
              'flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 px-3 text-sm font-medium transition-colors md:min-h-[40px] md:min-w-0',
              'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600',
              activa
                ? 'bg-brand-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800',
            )}
          >
            <Icono className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">{nombre}</span>
          </button>
        );
      })}
    </div>
  );
}
